// Runs a component-provided callback on a fixed interval while the component
// has asked for it and the browser tab is visible. It knows nothing about what
// the callback does: the consuming component owns the "should this still be
// refreshing?" decision and calls stopLiveRefresh() when the answer is no.
//
// A component using this mixin must:
//   - define `refreshIntervalMs` (data or computed) — tick spacing in ms,
//   - define an `onLiveRefresh()` method — the callback; when it returns a
//     promise the next tick is armed only once that promise settles,
//   - call `startLiveRefresh()` / `stopLiveRefresh()` (both idempotent).
//
// Ticks are re-armed with setTimeout after each callback rather than driven by
// setInterval, so a slow request can never queue overlapping ticks.

// How stale the data may be on returning to the tab before we refresh at once
// instead of waiting out the remaining interval.
const RESUME_THRESHOLD_MS = 10000

export default {
  data: () => ({
    liveRefreshActive: false,
    liveRefreshTimer: null,
    lastLiveRefreshAt: null,
  }),

  methods: {
    startLiveRefresh: function () {
      if (this.liveRefreshActive) return

      this.liveRefreshActive = true
      // Options-API methods are bound to the instance, so this same reference
      // is what removeEventListener needs later.
      document.addEventListener('visibilitychange', this.onLiveRefreshVisibilityChange)
      this.scheduleLiveRefresh()
    },

    stopLiveRefresh: function () {
      if (!this.liveRefreshActive) return

      this.liveRefreshActive = false
      this.clearLiveRefreshTimer()
      document.removeEventListener('visibilitychange', this.onLiveRefreshVisibilityChange)
    },

    clearLiveRefreshTimer: function () {
      if (this.liveRefreshTimer != null) {
        window.clearTimeout(this.liveRefreshTimer)
        this.liveRefreshTimer = null
      }
    },

    scheduleLiveRefresh: function (delayMs) {
      this.clearLiveRefreshTimer()
      if (!this.liveRefreshActive || document.hidden) return

      var delay = delayMs == null ? this.refreshIntervalMs : delayMs
      this.liveRefreshTimer = window.setTimeout(this.runLiveRefresh, delay)
    },

    runLiveRefresh: function () {
      this.liveRefreshTimer = null
      if (!this.liveRefreshActive || document.hidden) return

      this.lastLiveRefreshAt = Date.now()

      var result = null
      try {
        result = this.onLiveRefresh()
      } catch (e) {
        this.$log.warn(`Live refresh callback failed: ${e}`)
      }

      var reschedule = () => this.scheduleLiveRefresh()

      if (result != null && typeof result.then === 'function') {
        // Both handlers, so a rejected callback re-arms instead of leaving an
        // unhandled rejection and a dead timer.
        result.then(reschedule, (reason) => {
          this.$log.warn(`Live refresh callback failed: ${reason}`)
          reschedule()
        })
      } else {
        reschedule()
      }
    },

    onLiveRefreshVisibilityChange: function () {
      if (!this.liveRefreshActive) return

      if (document.hidden) {
        this.clearLiveRefreshTimer()
        return
      }

      var age = this.lastLiveRefreshAt == null
        ? Infinity
        : Date.now() - this.lastLiveRefreshAt

      this.scheduleLiveRefresh(age >= RESUME_THRESHOLD_MS ? 0 : this.refreshIntervalMs)
    },
  },

  beforeUnmount: function () {
    this.stopLiveRefresh()
  },
}
